import type { FormikErrors, FormikTouched } from "formik"
import { Formik } from "formik"
import React from "react"
import { useTranslation } from "react-i18next"
import { HiCheckCircle, HiExclamationCircle } from "react-icons/hi"
import * as Yup from "yup"

import { twClassNames } from "../../../../helpers"

type Props = {
  initialValues: EditProfileFormValues
  currentEmail: string
  isEmailVerified: boolean
  onCancel: () => void
  onSubmit: (values: EditProfileFormValues) => Promise<{ emailChanged?: boolean }>
}

export type EditProfileFormValues = {
  firstName: string
  lastName: string
  email: string
  language: string
}

const SUPPORTED_LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fi", label: "Suomi" }
]

export const EditProfileForm: React.FC<Props> = ({
  initialValues,
  currentEmail,
  isEmailVerified,
  onCancel,
  onSubmit
}) => {
  const { t } = useTranslation()

  const schema = Yup.object({
    firstName: Yup.string().required(t("account.personal.validation.firstNameRequired")),
    lastName: Yup.string().required(t("account.personal.validation.lastNameRequired")),
    email: Yup.string()
      .email(t("account.personal.validation.emailInvalid"))
      .required(t("account.personal.validation.emailRequired")),
    language: Yup.string().oneOf(["en", "fi"]).required()
  })

  const fieldClass = (
    name: keyof EditProfileFormValues,
    touched: FormikTouched<EditProfileFormValues>,
    errors: FormikErrors<EditProfileFormValues>
  ) => twClassNames("tw-form-control", touched[name] && errors[name] ? "tw-is-invalid" : "")

  return (
    <Formik<EditProfileFormValues>
      initialValues={initialValues}
      validationSchema={schema}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          await onSubmit(values)
          onCancel()
        } finally {
          setSubmitting(false)
        }
      }}>
      {formik => (
        <form className="tw-grid tw-grid-cols-6 tw-gap-4 tw-max-w-lg" onSubmit={formik.handleSubmit}>
          <h5 className="tw-col-span-6 tw-text-lg tw-font-semibold tw-mb-2">
            {t("account.personal.editProfile.title")}
          </h5>

          {/* First Name */}
          <div className="tw-relative tw-col-span-6 sm:tw-col-span-3">
            <label htmlFor="firstName" className="tw-form-label">
              {t("account.personal.form.firstName")}
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.firstName}
              className={fieldClass("firstName", formik.touched, formik.errors)}
            />
            <div className="tw-invalid-tooltip">{formik.errors.firstName}</div>
          </div>

          {/* Last Name */}
          <div className="tw-relative tw-col-span-6 sm:tw-col-span-3">
            <label htmlFor="lastName" className="tw-form-label">
              {t("account.personal.form.lastName")}
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.lastName}
              className={fieldClass("lastName", formik.touched, formik.errors)}
            />
            <div className="tw-invalid-tooltip">{formik.errors.lastName}</div>
          </div>

          {/* Email */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="email" className="tw-form-label tw-flex tw-items-center tw-gap-2">
              {t("account.personal.form.email")}
              {formik.values.email !== currentEmail ? (
                <span
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-xs tw-font-medium
                    tw-text-danger"
                  title={t("account.personal.emailChangeWarning")}>
                  <HiExclamationCircle className="tw-h-3.5 tw-w-3.5" />
                  {t("account.personal.emailChangeWarning")}
                </span>
              ) : isEmailVerified ? (
                <span
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-xs tw-font-medium
                    tw-text-success"
                  title={t("account.personal.emailVerified")}>
                  <HiCheckCircle className="tw-h-3.5 tw-w-3.5" />
                  {t("account.personal.emailVerified")}
                </span>
              ) : (
                <span
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-text-xs tw-font-medium
                    tw-text-danger"
                  title={t("account.personal.emailUnverified")}>
                  <HiExclamationCircle className="tw-h-3.5 tw-w-3.5" />
                  {t("account.personal.emailUnverified")}
                </span>
              )}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              className={fieldClass("email", formik.touched, formik.errors)}
            />
            <div className="tw-invalid-tooltip">{formik.errors.email}</div>
          </div>

          {/* Communication Language */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="language" className="tw-form-label">
              {t("account.personal.form.language")}
            </label>
            <select
              id="language"
              name="language"
              value={formik.values.language}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={fieldClass("language", formik.touched, formik.errors)}>
              {SUPPORTED_LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
            <div className="tw-invalid-tooltip">{formik.errors.language}</div>
          </div>

          {/* Actions */}
          <div className="tw-col-span-6 tw-mt-2 tw-flex tw-gap-3">
            <button type="button" className="tw-btn tw-btn-outline" onClick={onCancel} disabled={formik.isSubmitting}>
              {t("account.personal.button.cancel")}
            </button>
            <button type="submit" className="tw-btn tw-btn-primary" disabled={formik.isSubmitting}>
              {t("account.personal.button.save")}
            </button>
          </div>
        </form>
      )}
    </Formik>
  )
}
